import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { toNumber } from '@/lib/decimal'
import { formatContractHours } from '@/lib/format'
import { useHistorySuggestion } from '@/lib/planning-api'
import type { HistorySuggestionRow, MonthKey } from '@/lib/planning-types'
import { cn } from '@/lib/utils'
import { monthLabel } from './month'

interface HistoryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  month: MonthKey
  onApply: (rows: HistorySuggestionRow[]) => void
  applying?: boolean
}

const NEW_OPPORTUNITY = 'new'

/**
 * Het voorstel uit de historie. Je ziet wat er komt te staan voor het wordt
 * opgeslagen, kunt elk getal aanpassen en regels uitvinken; wat overblijft is
 * jouw keuze, niet een afleiding die vanzelf meebeweegt.
 *
 * Staat er al iets voor die persoon bij die klant, dan zegt de regel dat erbij:
 * toepassen vervangt de cel en zou eigen invoer anders ongezien overschrijven.
 */
export function HistoryModal({ open, onOpenChange, month, onApply, applying }: HistoryModalProps) {
  const suggestion = useHistorySuggestion(month, open)
  const [rows, setRows] = useState<HistorySuggestionRow[]>([])
  const [skipped, setSkipped] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open || !suggestion.data) return
    setRows(suggestion.data.rows)
    setSkipped(new Set())
  }, [open, suggestion.data])

  const keyOf = (row: HistorySuggestionRow) => `${row.personId}|${row.clientId}`
  const selected = rows.filter((row) => !skipped.has(keyOf(row)) && toNumber(row.hours) > 0)

  const patch = (key: string, change: Partial<HistorySuggestionRow>) =>
    setRows((current) => current.map((row) => (keyOf(row) === key ? { ...row, ...change } : row)))

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`${monthLabel(month)} vullen uit historie`}
      description={
        suggestion.data
          ? `Gemiddelde over ${suggestion.data.basedOn.months} afgeronde maanden.`
          : undefined
      }
      className="w-[min(92vw,680px)]"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={applying || selected.length === 0}
            onClick={() => onApply(selected)}
          >
            {selected.length} regel{selected.length === 1 ? '' : 's'} overnemen
          </button>
        </>
      }
    >
      {suggestion.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-soft">
          Er zijn in de afgelopen drie afgeronde maanden geen uren geschreven waaruit iets te voorspellen valt.
        </p>
      ) : (
        <ul className="divide-y divide-hairline">
          {rows.map((row) => {
            const key = keyOf(row)
            const off = skipped.has(key)
            const alreadyPlanned = toNumber(row.plannedHours)

            return (
              <li key={key} className={cn('flex items-center gap-3 py-2.5', off && 'opacity-45')}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={!off}
                  aria-label={`${row.clientName} voor ${row.personName} meenemen`}
                  onClick={() =>
                    setSkipped((current) => {
                      const next = new Set(current)
                      if (next.has(key)) next.delete(key)
                      else next.add(key)
                      return next
                    })
                  }
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
                    off ? 'border-hairline-strong bg-surface' : 'border-accent-600 bg-accent-600',
                  )}
                >
                  {off ? null : <Check className="h-3 w-3 text-canvas" strokeWidth={3} />}
                </button>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">
                    {row.clientName}
                    {row.internal ? <span className="ml-1.5 text-xs text-ink-faint">intern</span> : null}
                  </span>
                  <span className="block truncate text-xs text-ink-faint">
                    {row.personName}
                    {alreadyPlanned > 0 ? ` · vervangt de ${formatContractHours(alreadyPlanned)} u die er nu staat` : ''}
                  </span>
                </span>

                {row.options.length > 1 ? (
                  <Select
                    value={row.opportunityId ?? NEW_OPPORTUNITY}
                    onValueChange={(value) => patch(key, { opportunityId: value === NEW_OPPORTUNITY ? null : value })}
                    options={[
                      ...row.options.map((option) => ({ value: option.id, label: option.name })),
                      { value: NEW_OPPORTUNITY, label: row.proposedName },
                    ]}
                    disabled={off}
                    className="h-8 w-[190px] shrink-0 text-xs"
                  />
                ) : null}

                <span className="flex shrink-0 items-baseline gap-1">
                  <input
                    className="field num h-8 w-20 text-right text-sm"
                    inputMode="decimal"
                    aria-label={`Uren voor ${row.personName} bij ${row.clientName}`}
                    value={row.hours}
                    onChange={(event) => patch(key, { hours: event.target.value.replace(',', '.') })}
                    disabled={off}
                  />
                  <span className="text-xs text-ink-faint">u</span>
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <p className="mt-3 text-xs text-ink-faint">
        Klanten zonder lopende kans krijgen er een, op naam van de klant en meteen als gewonnen: lopend werk is werk dat
        al binnen is.
      </p>
    </Modal>
  )
}
